import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Form, Button, Card, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import Select, { SingleValue } from 'react-select';
import { getBreeds } from '../services/breedService';
import { uploadPup } from '../services/pupService';
import { Breed } from '../types/breed';
import { compressImage, getCroppedImage } from '../utils/imageCompression';

// Define interface for breed select options
interface BreedOption {
  value: string;
  label: string;
  isRare?: boolean;
}

interface UploadPageProps {
  defaultMode?: 'camera' | 'upload';
}

const UploadPage: React.FC<UploadPageProps> = ({ defaultMode = 'camera' }) => {
  const [captureMode, setCaptureMode] = useState<'camera' | 'upload'>(defaultMode);
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [selectedBreedOption, setSelectedBreedOption] = useState<BreedOption | null>(null);
  const [dogName, setDogName] = useState<string>('');
  const [caption, setCaption] = useState<string>('');
  const [locationName, setLocationName] = useState<string>('');
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [processingStatus, setProcessingStatus] = useState<string>('');

  // Crop state
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const navigate = useNavigate();

  // Sync URL path with captureMode on initial load
  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/upload/file') {
      setCaptureMode('upload');
    } else if (path === '/upload/camera') {
      setCaptureMode('camera');
    }
  }, []);

  // Fetch breeds for the dropdown
  useEffect(() => {
    const fetchBreeds = async () => {
      try {
        const data = await getBreeds();
        setBreeds(data);
      } catch (error) {
        setError('Failed to load dog breeds. Please try again later.');
      }
    };

    fetchBreeds();
  }, []);

  // Set default crop to full image when image loads
  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const fullCrop: Crop = { unit: 'px', x: 0, y: 0, width, height };
    setCrop(fullCrop);
    setCompletedCrop({ ...fullCrop, unit: 'px' });
  }, []);

  // Handle capturing image from webcam
  const captureImage = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setCapturedImage(imageSrc);
      setSelectedFile(null);
      setCrop(undefined);
      setCompletedCrop(undefined);
    }
  }, [webcamRef]);

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setCapturedImage(null);

      // Create a preview for the selected file
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedImage(reader.result as string);
      };
      reader.readAsDataURL(file);

      setCrop(undefined);
      setCompletedCrop(undefined);
    }
  };

  // Switch between camera and upload
  const toggleCaptureMode = (mode: 'camera' | 'upload') => {
    setCaptureMode(mode);
    setCapturedImage(null);
    setSelectedFile(null);
    setCameraError(null);
    setCrop(undefined);
    setCompletedCrop(undefined);

    // Update URL to match the mode
    if (mode === 'camera') {
      navigate('/upload/camera', { replace: true });
    } else {
      navigate('/upload/file', { replace: true });
    }
  };

  // Start over with a new image
  const handleRetake = () => {
    setCapturedImage(null);
    setSelectedFile(null);
    setCrop(undefined);
    setCompletedCrop(undefined);
  };

  // Handle form submission — crop + compress + upload in one step
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedBreedOption) {
      setError('Please select a dog breed');
      return;
    }

    if (!imgRef.current || !completedCrop) {
      setError('Please select an image first');
      return;
    }

    try {
      setError(null);
      setLoading(true);

      // Crop the image
      setProcessingStatus('Cropping image...');
      const croppedBlob = await getCroppedImage(imgRef.current, completedCrop);

      // Compress the cropped image
      setProcessingStatus('Compressing image...');
      const compressedBlob = await compressImage(croppedBlob, 3 * 1024 * 1024);

      // Build form data
      const formData = new FormData();
      formData.append('breed_id', selectedBreedOption.value);
      if (dogName) formData.append('dog_name', dogName);
      if (caption) formData.append('caption', caption);
      if (locationName) formData.append('location_name', locationName);

      const filename = selectedFile?.name || 'capture.jpg';
      formData.append('image', compressedBlob, filename);

      // Upload the pup
      setProcessingStatus('Uploading...');
      const result = await uploadPup(formData);

      // Redirect to the new pup's page
      navigate(`/pups/${result.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload pup');
    } finally {
      setLoading(false);
    }
  };

  // Determine which view to show for the image area
  const showCamera = captureMode === 'camera' && !capturedImage;
  const showFileUpload = captureMode === 'upload' && !capturedImage;
  const showCropper = !!capturedImage;

  return (
    <div>
      <h1 className="text-center mb-4">Upload a Pup</h1>

      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-center mb-3">
            <div className="btn-group">
              <Button
                variant={captureMode === 'camera' ? 'primary' : 'outline-primary'}
                className="me-2"
                onClick={() => toggleCaptureMode('camera')}
              >
                Use Camera
              </Button>
              <Button
                variant={captureMode === 'upload' ? 'primary' : 'outline-primary'}
                onClick={() => toggleCaptureMode('upload')}
              >
                Upload Image
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
          {cameraError && <Alert variant="warning">{cameraError}</Alert>}
          {loading && processingStatus && <Alert variant="info">{processingStatus}</Alert>}

          <Row>
            <Col md={6}>
              <div className="image-capture-area mb-3">
                {showCamera && (
                  <>
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      videoConstraints={{ facingMode: 'environment' }}
                      className="w-100"
                      onUserMediaError={() => setCameraError('Could not access camera. Please ensure you have granted camera permissions or try uploading an image instead.')}
                    />
                    <Button
                      variant="primary"
                      className="w-100 mt-2"
                      onClick={captureImage}
                    >
                      Capture Photo
                    </Button>
                  </>
                )}

                {showFileUpload && (
                  <div className="upload-area p-5 border rounded text-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Choose Image
                    </Button>
                    <p className="mt-2 text-muted">Select a dog photo from your device</p>
                  </div>
                )}

                {showCropper && (
                  <div>
                    <ReactCrop
                      crop={crop}
                      onChange={c => setCrop(c)}
                      onComplete={c => setCompletedCrop(c)}
                      keepSelection
                    >
                      <img
                        ref={imgRef}
                        src={capturedImage!}
                        alt="Crop preview"
                        onLoad={onImageLoad}
                        style={{ maxWidth: '100%', maxHeight: '60vh' }}
                      />
                    </ReactCrop>
                    <p className="text-muted text-center mt-2 mb-2" style={{ fontSize: '0.85rem' }}>
                      Drag edges to crop. Drag inside to move.
                    </p>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleRetake}
                    >
                      Choose Different Photo
                    </Button>
                  </div>
                )}
              </div>
            </Col>

            <Col md={6}>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="breedSelect">
                  <Form.Label>Dog Breed *</Form.Label>
                  <Select
                    className="breed-select"
                    classNamePrefix="breed-select"
                    placeholder="Select or search for a breed..."
                    options={breeds.map(breed => ({
                      value: String(breed.id),
                      label: `${breed.name}${breed.is_rare ? ' (Rare)' : ''}`,
                      isRare: breed.is_rare
                    }))}
                    value={selectedBreedOption}
                    onChange={(option: SingleValue<BreedOption>) => setSelectedBreedOption(option)}
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                  />
                  <Form.Text className="text-muted">
                    Type to search for a specific breed
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="dogName">
                  <Form.Label>Dog's Name (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter the dog's name"
                    value={dogName}
                    onChange={(e) => setDogName(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="caption">
                  <Form.Label>Caption (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Add a short caption"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    maxLength={100}
                  />
                  <Form.Text className="text-muted">
                    {caption.length}/100 characters
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="location">
                  <Form.Label>Location (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Where was this photo taken?"
                    value={locationName}
                    onChange={(e) => setLocationName(e.target.value)}
                  />
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 mt-3"
                  disabled={loading || !capturedImage || !selectedBreedOption}
                >
                  {loading ?
                    (processingStatus || 'Uploading...') :
                    'Upload Pup'}
                </Button>
              </Form>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default UploadPage;
